import type {
  AchievementUnlockedPayload,
  FriendAcceptedPayload,
  FriendRequestPayload,
  Notification,
  PartyInvitePayload,
  SeasonRewardPayload,
} from "@mmotdg/shared";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../api/AuthClient.js";
import { socialSocket } from "../../api/SocialSocket.js";
import { icon } from "../icons.js";

interface BellState {
  items: Notification[];
  unread: number;
  open: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;",
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function describe(n: Notification): { title: string; body: string } {
  switch (n.type) {
    case "friend_request": {
      const p = n.payload as FriendRequestPayload;
      return { title: "Friend request", body: `${p.fromUsername} wants to be your friend` };
    }
    case "friend_accepted": {
      const p = n.payload as FriendAcceptedPayload;
      return { title: "Friend accepted", body: `${p.byUsername} is now your friend` };
    }
    case "achievement_unlocked": {
      const p = n.payload as AchievementUnlockedPayload;
      const reward = [];
      if (p.rewardXp) reward.push(`+${p.rewardXp} XP`);
      if (p.rewardCoins) reward.push(`+${p.rewardCoins} coins`);
      return { title: `Achievement: ${p.name}`, body: reward.join(" · ") };
    }
    case "party_invite": {
      const p = n.payload as PartyInvitePayload;
      return { title: "Party invite", body: `${p.fromUsername} invited you to a party` };
    }
    case "season_reward": {
      const p = n.payload as SeasonRewardPayload;
      return { title: "Season ended", body: `Final rank #${p.finalRank ?? "—"} · +${p.rewardCoins} coins` };
    }
  }
}

export function mountNotificationBell(container: HTMLElement): () => void {
  const root = document.createElement("div");
  root.className = "notif-bell";
  root.innerHTML = `
    <button class="notif-trigger" type="button" aria-label="Notifications">
      ${icon("bell", 18)}
      <span class="notif-badge" hidden>0</span>
    </button>
    <div class="notif-dropdown" hidden>
      <div class="notif-dropdown-header">
        <span>Notifications</span>
        <button type="button" class="notif-mark-all">Mark all read</button>
      </div>
      <ul class="notif-list"></ul>
      <div class="notif-empty">No notifications yet</div>
    </div>
  `;
  container.appendChild(root);

  const state: BellState = { items: [], unread: 0, open: false };

  const trigger = root.querySelector<HTMLButtonElement>(".notif-trigger")!;
  const dropdown = root.querySelector<HTMLDivElement>(".notif-dropdown")!;
  const badge = root.querySelector<HTMLSpanElement>(".notif-badge")!;
  const list = root.querySelector<HTMLUListElement>(".notif-list")!;
  const empty = root.querySelector<HTMLDivElement>(".notif-empty")!;
  const markAllBtn = root.querySelector<HTMLButtonElement>(".notif-mark-all")!;

  const SOCIAL_POPOVER_OPEN_EVENT = "social-popover-open";
  const POPOVER_ID = "notifications";

  function setOpen(open: boolean): void {
    state.open = open;
    dropdown.hidden = !open;
    if (open) {
      document.dispatchEvent(new CustomEvent(SOCIAL_POPOVER_OPEN_EVENT, { detail: POPOVER_ID }));
      void refresh();
    }
  }

  trigger.addEventListener("click", () => setOpen(!state.open));

  const onDocClick = (e: MouseEvent): void => {
    if (!state.open) return;
    if (!root.contains(e.target as Node)) setOpen(false);
  };
  document.addEventListener("click", onDocClick);

  const onPopoverOpen = (e: Event): void => {
    const detail = (e as CustomEvent<string>).detail;
    if (detail !== POPOVER_ID && state.open) setOpen(false);
  };
  document.addEventListener(SOCIAL_POPOVER_OPEN_EVENT, onPopoverOpen);

  markAllBtn.addEventListener("click", async () => {
    try {
      await markAllNotificationsRead();
      state.items = state.items.map((n) => ({ ...n, readAt: n.readAt ?? Date.now() }));
      state.unread = 0;
      render();
    } catch {
      // ignore
    }
  });

  list.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const li = target.closest<HTMLLIElement>("li.notif-item");
    if (!li) return;
    const id = li.dataset.id;
    if (!id) return;
    const item = state.items.find((n) => n.id === id);
    if (!item || item.readAt !== null) return;
    void markNotificationRead(id).then(() => {
      item.readAt = Date.now();
      state.unread = Math.max(0, state.unread - 1);
      render();
    });
  });

  async function refresh(): Promise<void> {
    try {
      const data = await listNotifications();
      state.items = data.items;
      state.unread = data.unread;
      render();
    } catch {
      // ignore
    }
  }

  function render(): void {
    if (state.unread > 0) {
      badge.hidden = false;
      badge.textContent = state.unread > 99 ? "99+" : String(state.unread);
    } else {
      badge.hidden = true;
    }
    if (state.items.length === 0) {
      list.innerHTML = "";
      empty.hidden = false;
    } else {
      empty.hidden = true;
      list.innerHTML = state.items
        .map((n) => {
          const { title, body } = describe(n);
          return `
            <li class="notif-item ${n.readAt ? "" : "unread"}" data-id="${n.id}">
              <div class="notif-title">${escapeHtml(title)}</div>
              <div class="notif-body">${escapeHtml(body)}</div>
              <div class="notif-time">${relativeTime(n.createdAt)}</div>
            </li>
          `;
        })
        .join("");
    }
  }

  const unsubscribe = socialSocket.onMessage((msg) => {
    if (msg.type === "notification_new") {
      state.items = [msg.notification, ...state.items].slice(0, 100);
      state.unread += 1;
      render();
    }
  });

  void refresh();

  return () => {
    unsubscribe();
    document.removeEventListener("click", onDocClick);
    document.removeEventListener(SOCIAL_POPOVER_OPEN_EVENT, onPopoverOpen);
    root.remove();
  };
}
